import { useState } from 'react'
import {
  type AdminRole,
  roleHasUsers,
  roleIdFromSlug,
  useAdminRoles,
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
} from '../../../features/admin/use-admin-roles'
import { AdminPage, adminError, Meta, rowCls } from './admin-bits'
import { Btn, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, td, th } from './controls'
import { mono, Panel } from './ui'

function RoleDialog({ role, onClose }: { role: AdminRole | null; onClose: () => void }) {
  const create = useCreateRole()
  const update = useUpdateRole()
  const [name, setName] = useState(role?.name ?? '')
  const [desc, setDesc] = useState(role?.description ?? '')
  const m = role ? update : create
  const valid = name.trim() !== ''
  const submit = () => {
    if (!valid || m.isPending) return
    const opts = { onSuccess: onClose }
    if (role) update.mutate({ roleId: role.id, name: name.trim(), description: desc.trim() }, opts)
    else create.mutate({ id: roleIdFromSlug(name), name: name.trim(), description: desc.trim() || undefined }, opts)
  }
  return (
    <Dialog
      open
      title={role ? 'Edit role' : 'Create role'}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Btn onClick={onClose} disabled={m.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" onClick={submit} disabled={!valid || m.isPending}>
            {m.isPending ? 'Saving...' : role ? 'Save' : 'Create'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {role ? null : (
        <p className="text-[11px] text-[var(--color-muted)]">
          id <span style={mono}>{roleIdFromSlug(name)}</span>
        </p>
      )}
      <Field label="Description">
        <input className={inputCls} value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      {m.error ? <Inline tone="bad">{adminError(m.error)}</Inline> : null}
    </Dialog>
  )
}

export function SignalAdminRoles() {
  const roles = useAdminRoles()
  const del = useDeleteRole()
  const [edit, setEdit] = useState<AdminRole | 'new' | null>(null)
  const [remove, setRemove] = useState<AdminRole | null>(null)
  const list = roles.data ?? []
  return (
    <AdminPage
      page="roles"
      title="Roles"
      meta={<Meta>{list.length} roles</Meta>}
      loading={roles.isLoading}
      error={!!roles.error}
      right={
        <Btn variant="primary" onClick={() => setEdit('new')}>
          Create role
        </Btn>
      }
    >
      <Panel title="Custom roles">
        {list.length === 0 ? (
          <Empty>No roles yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Id</th>
                  <th className={th}>Description</th>
                  <th className={th}>Users</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className={rowCls}>
                    <td className={`${td} font-medium`}>{r.name}</td>
                    <td className={td} style={mono}>
                      {r.id}
                    </td>
                    <td className={`${td} text-[var(--color-muted)]`}>{r.description ?? '-'}</td>
                    <td className={td} style={mono}>
                      {r.userCount}
                    </td>
                    <td className={`${td} text-right`}>
                      <span className="inline-flex gap-1">
                        <Btn onClick={() => setEdit(r)}>Edit</Btn>
                        <Btn
                          variant="danger"
                          disabled={roleHasUsers(r)}
                          title={roleHasUsers(r) ? 'Reassign its users first' : undefined}
                          onClick={() => setRemove(r)}
                        >
                          Delete
                        </Btn>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {edit ? <RoleDialog role={edit === 'new' ? null : edit} onClose={() => setEdit(null)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete role"
        body={<>Role {remove?.name} and its permission grants will be removed.</>}
        confirmLabel="Delete"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ roleId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}
